'use client';
import { useState, useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { motion } from 'framer-motion';
import { User, Lock, Eye, EyeOff, ArrowLeft, LogIn, ChevronDown } from 'lucide-react';
import Link from 'next/link';
import { Button, Input, Card } from '../../components/ui';
import toast from 'react-hot-toast';

const USER_TYPE_CONFIG = {
  1: { name: 'Student', loginLabel: 'Email Address', loginType: 'email', color: 'from-blue-500 to-purple-600' },
  2: { name: 'College', loginLabel: 'Username', loginType: 'text', color: 'from-teal-500 to-green-600' },
  3: { name: 'Staff', loginLabel: 'Username', loginType: 'text', color: 'from-green-500 to-blue-600' },
  4: { name: 'Employer', loginLabel: 'Username', loginType: 'text', color: 'from-orange-500 to-red-600' },
  5: { name: 'Sponsor', loginLabel: 'Username', loginType: 'text', color: 'from-pink-500 to-rose-600' },
  6: { name: 'Teacher', loginLabel: 'Username', loginType: 'text', color: 'from-indigo-500 to-blue-600' },
  7: { name: 'Accountant', loginLabel: 'Username', loginType: 'text', color: 'from-amber-500 to-orange-600' },
  8: { name: 'Mentor', loginLabel: 'Username', loginType: 'text', color: 'from-cyan-500 to-teal-600' },
  9: { name: 'Master Admin', loginLabel: 'Username', loginType: 'text', color: 'from-red-500 to-pink-600' },
  10: { name: 'Admin', loginLabel: 'Username', loginType: 'text', color: 'from-purple-500 to-indigo-600' },
};

export default function UnifiedLogin() {
  const [userType, setUserType] = useState('');
  const [loginId, setLoginId] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const router = useRouter();

  const selectedConfig = userType ? USER_TYPE_CONFIG[userType] : null;
  const gradientColor = selectedConfig?.color || 'from-blue-500 to-purple-600';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userType: parseInt(userType),
          loginId,
          password,
        }),
      });

      const data = await res.json();

      if (res.ok) {
        localStorage.setItem(data.storageKey, JSON.stringify(data.user));
        localStorage.setItem('userType', data.userTypeName);
        toast.success(`Login successful! Welcome to ${data.userTypeName} portal!`);
        router.push(data.redirect);
      } else {
        setError(data.message || 'Login failed');
        toast.error(data.message || 'Login failed');
      }
    } catch (err) {
      const errorMsg = 'Network error. Please check your connection.';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
      {/* Background decorations */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-32 h-32 bg-blue-200 rounded-full opacity-30 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-24 h-24 bg-purple-200 rounded-full opacity-30 animate-pulse delay-1000"></div>
        <div className="absolute top-1/2 left-1/4 w-16 h-16 bg-green-200 rounded-full opacity-30 animate-pulse delay-2000"></div>
        <div className="absolute top-1/3 right-1/4 w-20 h-20 bg-pink-200 rounded-full opacity-30 animate-pulse delay-3000"></div>
      </div>

      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-md relative z-10"
      >
        {/* Back to home */}
        <Link href="/" className="inline-flex items-center text-gray-600 hover:text-gray-900 mb-8 transition-colors">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Home
        </Link>

        <Card className="p-8" hover={false}>
          {/* Header */}
          <div className="text-center mb-8">
            <motion.div
              key={gradientColor}
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.3 }}
              className="flex justify-center mb-4"
            >
              <div className={`w-16 h-16 bg-gradient-to-br ${gradientColor} rounded-full flex items-center justify-center`}>
                <LogIn className="h-8 w-8 text-white" />
              </div>
            </motion.div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {selectedConfig ? `${selectedConfig.name} Portal` : 'StartupWorld Login'}
            </h1>
            <p className="text-gray-600">
              {selectedConfig
                ? `Sign in to your ${selectedConfig.name.toLowerCase()} account`
                : 'Select your role and sign in to continue'}
            </p>
          </div>

          {/* Login Form */}
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* User Type Dropdown */}
            <div className="space-y-1">
              <label className="block text-sm font-medium text-gray-700">
                User Type <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <select
                  value={userType}
                  onChange={(e) => {
                    setUserType(e.target.value);
                    setLoginId('');
                    setPassword('');
                    setError('');
                  }}
                  required
                  className="block w-full rounded-lg border border-gray-300 pl-3 pr-10 py-2.5 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors duration-200 appearance-none bg-white"
                >
                  <option value="">-- Select User Type --</option>
                  {Object.entries(USER_TYPE_CONFIG).map(([id, config]) => (
                    <option key={id} value={id}>
                      {config.name}
                    </option>
                  ))}
                </select>
                <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
              </div>
            </div>

            {/* Login ID */}
            <Input
              label={selectedConfig?.loginLabel || 'Login ID'}
              type={selectedConfig?.loginType || 'text'}
              placeholder={`Enter your ${selectedConfig?.loginLabel?.toLowerCase() || 'login ID'}`}
              value={loginId}
              onChange={(e) => setLoginId(e.target.value)}
              icon={User}
              required
              disabled={!userType}
            />

            {/* Password */}
            <div className="relative">
              <Input
                label="Password"
                type={showPassword ? 'text' : 'password'}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                icon={Lock}
                required
                disabled={!userType}
                error={error}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-8 text-gray-400 hover:text-gray-600 transition-colors"
              >
                {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>

            {/* Remember me & Forgot password */}
            <div className="flex items-center justify-between">
              <label className="flex items-center">
                <input type="checkbox" className="rounded border-gray-300 text-blue-600 focus:ring-blue-500" />
                <span className="ml-2 text-sm text-gray-600">Remember me</span>
              </label>
              <a href="#" className="text-sm text-blue-600 hover:text-blue-700 transition-colors">
                Forgot password?
              </a>
            </div>

            {/* Submit */}
            <Button
              type="submit"
              loading={loading}
              disabled={!userType || !loginId || !password}
              className={`w-full bg-gradient-to-r ${gradientColor} hover:opacity-90`}
              size="lg"
            >
              Sign In
            </Button>
          </form>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              Don&apos;t have an account?{' '}
              <a href="#" className="text-blue-600 hover:text-blue-700 font-medium transition-colors">
                Contact your administrator
              </a>
            </p>
          </div>
        </Card>

        {/* Quick login links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.4, duration: 0.6 }}
          className="mt-6"
        >
          <p className="text-center text-sm text-gray-500 mb-3">Or login directly as:</p>
          <div className="flex flex-wrap justify-center gap-2">
            <Link href="/student/login" className="text-xs bg-white border border-gray-200 rounded-full px-3 py-1.5 text-blue-600 hover:bg-blue-50 transition-colors">
              Student
            </Link>
            <Link href="/staff/login" className="text-xs bg-white border border-gray-200 rounded-full px-3 py-1.5 text-green-600 hover:bg-green-50 transition-colors">
              Staff
            </Link>
            <Link href="/teamleader/login" className="text-xs bg-white border border-gray-200 rounded-full px-3 py-1.5 text-purple-600 hover:bg-purple-50 transition-colors">
              Team Leader
            </Link>
          </div>
        </motion.div>
      </motion.div>
    </div>
  );
}
